import { useEffect, useState } from "react";
import { adminApi, getApiErrorMessage } from "@/lib/api";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Database, Download, Link2, Mail, RefreshCw, Upload, Users } from "lucide-react";
import { toast } from "sonner";

const FEEDS = [
  ["AUTO", "Auto-detect report"],
  ["TRANSACTIONS", "Transaction history"],
  ["FOLIOS", "Folio master"],
  ["SIPS", "SIP registrations"],
  ["HOLDINGS", "Holdings / AUM"],
  ["SCHEME_MASTER", "RTA scheme master"],
  ["BROKERAGE", "Brokerage / commission"],
];

const metricLabels = {
  transactions: "Transactions",
  folios: "Folios",
  sips: "SIPs",
  holdings: "Holdings",
  scheme_master: "Scheme mappings",
  brokerage: "Brokerage rows",
  mapped_clients: "Mapped clients",
  bse_uccs: "BSE UCCs",
};

const badge = (status) => {
  if (["COMPLETED", "ACCEPTED", "ACTIVE", "CLEAN"].includes(status)) return "bg-emerald-50 text-emerald-700";
  if (["FAILED", "QUARANTINED", "INACTIVE"].includes(status)) return "bg-red-50 text-red-700";
  return "bg-amber-50 text-amber-700";
};

export default function RtaFeeds() {
  const [dashboard, setDashboard] = useState({ counts: {}, recent_batches: [] });
  const [exceptions, setExceptions] = useState([]);
  const [folios, setFolios] = useState([]);
  const [uccs, setUccs] = useState([]);
  const [source, setSource] = useState("CAMS");
  const [feedType, setFeedType] = useState("AUTO");
  const [autoProvision, setAutoProvision] = useState(true);
  const [busy, setBusy] = useState(false);
  const [mapping, setMapping] = useState({});

  const load = async () => {
    try {
      const [dash, errs, folioRows, bseRows] = await Promise.all([
        adminApi.get("/admin/arn/dashboard"),
        adminApi.get("/admin/arn/exceptions?limit=300"),
        adminApi.get("/admin/arn/folios?limit=500"),
        adminApi.get("/admin/arn/bse-ucc?limit=500"),
      ]);
      setDashboard(dash.data);
      setExceptions(errs.data);
      setFolios(folioRows.data);
      setUccs(bseRows.data);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  useEffect(() => { load(); }, []);

  const upload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    const body = new FormData();
    files.forEach((file) => body.append("files", file));
    body.append("source", source);
    body.append("feed_type", feedType);
    body.append("auto_provision", String(autoProvision));
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/arn/ingest", body);
      const accepted = data.files.reduce((sum, item) => sum + (item.accepted || 0), 0);
      const held = data.files.reduce((sum, item) => sum + (item.quarantined || 0), 0);
      const direct = data.files.reduce((sum, item) => sum + (item.excluded_direct || 0), 0);
      toast.success(`${data.processed} report(s) ingested · ${accepted} accepted · ${held} held for review · ${direct} Direct Plan rows excluded`);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const downloadSource = async (batch) => {
    try {
      const response = await adminApi.get(`/admin/arn/batches/${batch.id}/source`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = batch.file_name || "rta-report";
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const dispatchAccess = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/arn/access/dispatch");
      if (data.reason) toast.error(`Client access not sent: ${data.reason.replaceAll("_", " ")}`);
      else toast.success(`${data.sent} activation e-mail(s) sent through Microsoft Graph; ${data.pending} pending`);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const syncUcc = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/arn/bse-ucc/sync");
      toast.success(`${data.fetched || 0} BSE UCC(s) read · ${data.mapped || 0} mapped · ${data.unmatched || 0} unmatched`);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const mapException = async (row, kind) => {
    const value = (mapping[row.id] || "").trim();
    if (!value) return toast.error(`Enter a ${kind === "client" ? "client ID" : "Regular Plan scheme code"}`);
    try {
      await adminApi.post(`/admin/arn/exceptions/${row.id}/map-${kind}`, {
        [kind === "client" ? "user_id" : "scheme_code"]: value,
      });
      toast.success(`${kind === "client" ? "Client" : "Scheme"} mapping saved`);
      setMapping((old) => ({ ...old, [row.id]: "" }));
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl text-[var(--brand)]">ARN Data Hub</h1>
          <p className="mt-1 text-sm text-[var(--ink-muted)]">Authoritative CAMS, KFintech and BSE UCC ingestion for the SMIFS mutual-fund book.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={busy}><RefreshCw className="mr-2 h-4 w-4" />Refresh</Button>
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Object.entries(metricLabels).map(([key, label]) => (
          <Card key={key} className="border-[var(--glass-border)]">
            <CardContent className="p-4">
              <div className="text-xs text-[var(--ink-muted)]">{label}</div>
              <div className="mt-1 font-heading text-2xl text-[var(--brand)]">{dashboard.counts?.[key] || 0}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="glass-card mb-6 border-0">
        <CardHeader><CardTitle className="flex items-center gap-2 font-heading text-lg text-[var(--brand)]"><Upload className="h-5 w-5" />Ingest RTA delivery</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <select className="rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={source} onChange={(e) => setSource(e.target.value)}>
              <option value="CAMS">CAMS</option><option value="KFINTECH">KFintech</option>
            </select>
            <select className="rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={feedType} onChange={(e) => setFeedType(e.target.value)}>
              {FEEDS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-[var(--ink-muted)]">
              <input type="checkbox" checked={autoProvision} onChange={(e) => setAutoProvision(e.target.checked)} />
              Create access for uniquely matched PAN + e-mail
            </label>
            <label className={`flex cursor-pointer items-center rounded-full bg-[var(--cta)] px-4 py-2 text-sm font-medium text-white ${busy ? "pointer-events-none opacity-60" : ""}`}>
              <Upload className="mr-2 h-4 w-4" />{busy ? "Processing…" : "Select report files"}
              <input className="hidden" type="file" multiple accept=".csv,.txt,.dat,.tsv,.psv,.xls,.xlsx,.xlsm,.dbf,.zip" onChange={upload} disabled={busy} />
            </label>
          </div>
          <p className="mt-3 text-xs text-[var(--ink-muted)]">Every source file and row is retained with its SHA-256 fingerprint. Direct Plan rows are excluded; ambiguous identities and schemes are quarantined.</p>
        </CardContent>
      </Card>

      <Tabs defaultValue="batches">
        <TabsList className="flex h-auto flex-wrap">
          <TabsTrigger value="batches"><Database className="mr-1.5 h-4 w-4" />Batches ({dashboard.counts?.batches || 0})</TabsTrigger>
          <TabsTrigger value="exceptions"><AlertTriangle className="mr-1.5 h-4 w-4" />Exceptions ({dashboard.counts?.exceptions || 0})</TabsTrigger>
          <TabsTrigger value="folios"><Users className="mr-1.5 h-4 w-4" />Folios ({folios.length})</TabsTrigger>
          <TabsTrigger value="ucc"><Link2 className="mr-1.5 h-4 w-4" />BSE UCC registry ({uccs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="batches">
          <Card className="border-[var(--glass-border)]"><CardContent className="overflow-x-auto p-0">
            {!dashboard.recent_batches?.length ? <div className="p-6 text-sm text-[var(--ink-muted)]">No RTA delivery has been ingested.</div> : (
              <table className="w-full min-w-[900px] text-xs">
                <thead><tr className="border-b text-left text-[var(--ink-muted)]"><th className="p-3">File</th><th>Source</th><th>Report</th><th>Rows</th><th>Accepted</th><th>Exceptions</th><th>Direct excluded</th><th>Reconciliation</th><th>Status</th><th /></tr></thead>
                <tbody className="divide-y">
                  {dashboard.recent_batches.map((batch) => <tr key={batch.id}>
                    <td className="max-w-[260px] truncate p-3 font-medium" title={batch.file_name}>{batch.file_name}</td>
                    <td>{batch.source}</td><td>{batch.feed_type}</td><td>{batch.total_rows || 0}</td><td>{batch.accepted || 0}</td><td>{batch.quarantined || 0}</td><td>{batch.excluded_direct || 0}</td><td>{batch.reconciliation_status ? <span className={`rounded-full px-2 py-1 ${badge(batch.reconciliation_status)}`}>{batch.reconciliation_status} · {batch.matched || 0} matched / {batch.platform_only || 0} missing</span> : "—"}</td>
                    <td><span className={`rounded-full px-2 py-1 ${badge(batch.status)}`}>{batch.status}</span></td>
                    <td><Button size="sm" variant="ghost" onClick={() => downloadSource(batch)}><Download className="h-4 w-4" /></Button></td>
                  </tr>)}
                </tbody>
              </table>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="exceptions">
          <Card className="border-[var(--glass-border)]"><CardContent className="p-0">
            {!exceptions.length ? <div className="p-6 text-sm text-emerald-700">No unresolved ingestion exceptions.</div> : <div className="divide-y">
              {exceptions.map((row) => {
                const schemeIssue = row.issues?.some((issue) => issue.includes("SCHEME"));
                return <div key={row.id} className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div><div className="text-sm font-medium">{row.canonical?.investor_name || row.canonical?.scheme_name || `Row ${row.row_no}`}</div><div className="text-xs text-[var(--ink-muted)]">{row.source} · {row.feed_type} · folio {row.canonical?.folio_no || "—"} · PAN {row.canonical?.pan || "—"}</div></div>
                    <span className="rounded-full bg-red-50 px-2 py-1 text-xs text-red-700">{row.issues?.join(" · ")}</span>
                  </div>
                  <div className="mt-3 flex max-w-xl flex-col gap-2 sm:flex-row">
                    <input className="min-w-0 flex-1 rounded-md border p-2 text-xs" value={mapping[row.id] || ""} onChange={(e) => setMapping((old) => ({ ...old, [row.id]: e.target.value }))} placeholder={schemeIssue ? "Regular Plan scheme code" : "Existing client ID"} />
                    <Button size="sm" onClick={() => mapException(row, schemeIssue ? "scheme" : "client")}>Map {schemeIssue ? "scheme" : "client"}</Button>
                  </div>
                </div>;
              })}
            </div>}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="folios">
          <Card className="border-[var(--glass-border)]"><CardContent className="overflow-x-auto p-0">
            {!folios.length ? <div className="p-6 text-sm text-[var(--ink-muted)]">No folio master imported.</div> : <table className="w-full min-w-[850px] text-xs">
              <thead><tr className="border-b text-left text-[var(--ink-muted)]"><th className="p-3">Folio</th><th>Investor</th><th>PAN</th><th>Scheme</th><th>Mode</th><th>Source</th><th>Client</th></tr></thead>
              <tbody className="divide-y">{folios.map((folio) => <tr key={`${folio.source}:${folio.fingerprint}`}><td className="p-3 font-medium">{folio.folio_no}</td><td>{folio.investor_name}</td><td>{folio.pan || "—"}</td><td className="max-w-[280px] truncate">{folio.scheme_name || "—"}</td><td>{folio.holding_mode || "PHYSICAL"}</td><td>{folio.source}</td><td>{folio.user_id || "Unmapped"}</td></tr>)}</tbody>
            </table>}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="ucc">
          <Card className="border-[var(--glass-border)]">
            <CardHeader className="gap-3 sm:flex-row sm:items-center sm:justify-between"><CardTitle className="font-heading text-base text-[var(--brand)]">BSE UCC mapping</CardTitle><Button size="sm" onClick={syncUcc} disabled={busy}><RefreshCw className="mr-2 h-4 w-4" />Read BSE registry</Button></CardHeader>
            <CardContent className="overflow-x-auto p-0">
              {!uccs.length ? <div className="p-6 text-sm text-[var(--ink-muted)]">No BSE UCC registry has been read yet.</div> : <table className="w-full min-w-[850px] text-xs">
                <thead><tr className="border-b text-left text-[var(--ink-muted)]"><th className="p-3">UCC</th><th>Investor</th><th>Mode</th><th>Status</th><th>Mapping</th><th>Client</th></tr></thead>
                <tbody className="divide-y">{uccs.map((item) => <tr key={item.ucc}><td className="p-3 font-medium">{item.ucc}</td><td>{item.investor_name || "—"}</td><td>{item.holding_mode}</td><td><span className={`rounded-full px-2 py-1 ${badge(item.status)}`}>{item.status || "UNKNOWN"}</span></td><td>{item.mapping_method}</td><td>{item.user_id || "Unmatched"}</td></tr>)}</tbody>
              </table>}
            </CardContent>
          </Card>
          <div className="mt-3 flex flex-col items-start gap-3 rounded-xl border border-[var(--glass-border)] p-4 text-sm sm:flex-row sm:items-center sm:justify-between"><span><Mail className="mr-2 inline h-4 w-4" />{dashboard.counts?.access_pending || 0} client activation(s) pending Graph delivery</span><Button variant="outline" size="sm" onClick={dispatchAccess} disabled={busy}>Send activation access</Button></div>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
